'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import {
  Languages,
  Mic,
  MonitorUp,
  Type,
  UserCircle,
  Wand2,
  Zap,
  ZoomIn,
  type LucideIcon
} from 'lucide-react';
import { motion } from 'motion/react';

import { Button } from '@workspace/ui/components/button';
import { useTheme } from '@workspace/ui/hooks/use-theme';

type Feature = {
  icon: LucideIcon;
  title: string;
  description: string;
  lightImage: string;
  darkImage: string;
  span: string;
  comingSoon?: boolean;
};

const features: Feature[] = [
  {
    icon: UserCircle,
    title: 'AI Avatar Integration',
    description:
      'Use AI avatars to present your content, explain steps, or guide viewers without recording yourself.',
    lightImage: '/assets/homepage/Avatar.png',
    darkImage: '/assets/homepage/Avatar-dark.png',
    span: 'col-span-1'
  },
  {
    icon: ZoomIn,
    title: 'Zoom & Focus Animations',
    description:
      'Automatically add zoom-ins and motion effects to highlight important actions in your recording and guide viewer attention clearly.',
    lightImage: '/assets/homepage/features/5-zoom-focus-light.svg',
    darkImage: '/assets/homepage/features/5-zoom-focus-dark.svg',
    span: 'col-span-1'
  },
  {
    icon: Type,
    title: 'Add Callouts & On-Screen Text',
    description:
      'Explain steps visually with text overlays, annotations, and callouts that make your videos easier to follow.',
    lightImage: '/assets/homepage/features/6-callouts-light.svg',
    darkImage: '/assets/homepage/features/6-callouts-dark.svg',
    span: 'col-span-1'
  },
  {
    icon: Languages,
    title: 'Translate Videos to Any Language',
    description:
      'Turn one video into multiple versions with AI-generated voice and captions across different languages.',
    lightImage: '/assets/homepage/features/1-record-upload-light.svg',
    darkImage: '/assets/homepage/features/1-record-upload-dark.svg',
    span: 'col-span-1'
  },
  {
    icon: MonitorUp,
    title: 'Record or Upload Instantly',
    description:
      'Record your screen instantly with browser-based recording, screen selection, and quick preview.',
    lightImage: '/assets/homepage/features/3-record-upload-light.svg',
    darkImage: '/assets/homepage/features/3-record-upload-dark.svg',
    span: 'col-span-1'
  },
  {
    icon: Wand2,
    title: 'Built-in Animation Effects',
    description:
      'Apply smooth transitions and animation styles to make your videos feel polished and professional.',
    lightImage: '/assets/homepage/features/9-animations-light.svg',
    darkImage: '/assets/homepage/features/9-animations-dark.svg',
    span: 'col-span-1'
  },
  {
    icon: Mic,
    title: 'Add Your Own Custom Voice',
    description:
      'Connect your ElevenLabs account to clone and import your own voice, then narrate every video in a voice that is unmistakably yours.',
    lightImage: '/assets/homepage/features/2-ai-voice-light.svg',
    darkImage: '/assets/homepage/features/2-ai-voice-dark.svg',
    span: 'col-span-1'
  },
  {
    icon: Zap,
    title: 'Free Unlimited Local Renders',
    description:
      'Render your final video right in your browser — free, unlimited, up to 4K, and it never uses your monthly render quota on paid plans.',
    lightImage: '/assets/homepage/features/8-fast-render-light.svg',
    darkImage: '/assets/homepage/features/8-fast-render-dark.svg',
    span: 'col-span-1'
  }
  // {
  //   icon: ImagePlus,
  //   title: 'Import Your Own Media',
  //   description:
  //     'Enhance your videos by adding images, clips, and audio to support your explanation and improve clarity.',
  //   lightImage: '/assets/homepage/features/7-import-media-light.svg',
  //   darkImage: '/assets/homepage/features/7-import-media-dark.svg',
  //   span: 'col-span-1'
  // },
  // {
  //   icon: Mic,
  //   title: 'Advanced AI Voice System',
  //   description:
  //     'Convert your script into natural voiceovers with multiple styles, tones, and voice options.',
  //   lightImage: '/assets/homepage/features/2-ai-voice-light.svg',
  //   darkImage: '/assets/homepage/features/2-ai-voice-dark.svg',
  //   span: 'col-span-1'
  // }
];

export default function FeaturesSection() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const isDark = mounted && resolvedTheme === 'dark';

  // Bento grid layout: 2 + 3 + 3
  const row1 = features.slice(0, 2);
  const row2 = features.slice(2, 5);
  const row3 = features.slice(5, 8);

  return (
    <section
      id="features"
      className="py-14 sm:py-20 md:py-24 bg-section-features"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="text-center mb-8 sm:mb-16">
          <span className="inline-flex items-center rounded-full border border-primary/20 px-3 py-1 text-xs font-medium text-primary mb-4 bg-videomule-light-green dark:bg-primary/10">
            Features
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground">
            Everything you need to create tutorials
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto text-sm sm:text-base md:text-lg">
            A complete toolkit for turning screen recordings into polished,
            narrated tutorial videos.
          </p>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {/* Row 1: 2 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {row1.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="group rounded-2xl border border-border bg-card p-5 sm:p-6 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-base sm:text-lg text-foreground">
                      {feature.title}
                    </h3>
                    {feature.comingSoon && (
                      <span className="inline-flex items-center rounded-full border border-amber-500 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap bg-amber-50 dark:bg-amber-500/10">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <div className="rounded-xl overflow-hidden border border-border">
                    <Image
                      src={isDark ? feature.darkImage : feature.lightImage}
                      alt={feature.title}
                      width={600}
                      height={400}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Row 2: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {row2.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                  className="group rounded-2xl border border-border bg-card p-5 sm:p-6 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-base sm:text-lg text-foreground">
                      {feature.title}
                    </h3>
                    {feature.comingSoon && (
                      <span className="inline-flex items-center rounded-full border border-amber-500 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <div className="rounded-xl overflow-hidden border border-border">
                    <Image
                      src={isDark ? feature.darkImage : feature.lightImage}
                      alt={feature.title}
                      width={600}
                      height={400}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Row 3: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {row3.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
                  className="group rounded-2xl border border-border bg-card p-5 sm:p-6 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-base sm:text-lg text-foreground">
                      {feature.title}
                    </h3>
                    {feature.comingSoon && (
                      <span className="inline-flex items-center rounded-full border border-amber-500 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <div className="rounded-xl overflow-hidden border border-border">
                    <Image
                      src={isDark ? feature.darkImage : feature.lightImage}
                      alt={feature.title}
                      width={600}
                      height={400}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Row 4: 3 cards */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {row4.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.6 + i * 0.1 }}
                  className="group rounded-2xl border border-border bg-card p-5 sm:p-6 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-base sm:text-lg text-foreground">
                      {feature.title}
                    </h3>
                    {feature.comingSoon && (
                      <span className="inline-flex items-center rounded-full border border-amber-500 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <div className="rounded-xl overflow-hidden border border-border">
                    <Image
                      src={isDark ? feature.darkImage : feature.lightImage}
                      alt={feature.title}
                      width={600}
                      height={400}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div> */}
        </div>

        {/* CTA */}
        <div className="text-center mt-10 sm:mt-14">
          <a href="#pricing">
            <Button
              size="lg"
              className="rounded-full px-8 py-3 text-base font-semibold"
            >
              See Pricing
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}
